/**
 * ATHÉR - previous/next buttons and a position indicator for Horizon's phone
 * carousel (a collection list with "carousel on mobile").
 *
 * The section's card block writes one small JSON config per card (settings
 * and translated labels); the first one found for a section is used. The nav
 * is inserted after that section's phone carousel, which Horizon already
 * hides from 750px up, so it exists on phones only.
 *
 * Nothing polls. The indicator follows the scroller's own `scroll` event
 * (coalesced to one frame), so a touch swipe and a button press update it the
 * same way. RTL: Chrome and Safari report a negative scrollLeft in a
 * right-to-left scroller, so the position is read as |scrollLeft| and "next"
 * scrolls towards the reading end. Reduced motion: jumps instead of gliding.
 *
 * Without JavaScript nothing is inserted and the carousel still swipes.
 *
 * Other ATHÉR carousels (the collection intro's phone cards) use the same nav
 * by naming their parts in the config: "carousel" (where the nav goes),
 * "scroller" (the element that scrolls) and "slide" (a card's class).
 *
 * Rows that show several cards at once (the product page rails) add
 * "multi": true - the counter shows the first visible card, "next" stops at
 * the end of travel, and the nav hides itself while every card fits. They can
 * also ask for "keys" (arrow keys, Home and End on the focusable scroller) and
 * "drag" (mouse drag; touch keeps its native swipe). Content inserted later
 * calls window.AtherCarouselNav.scan(root).
 *
 * "loop": true makes the row endless - at the end the first card moves behind the
 * last (the view held still while it moves), so "next" keeps going one card at a
 * time with no rewind; "previous" does the same the other way. The counter shows
 * the card's place in the original order.
 * "autoplay": <seconds> moves one card forward on that beat from "autoplayMin"
 * px of width up (default 990). It waits while the pointer is over the row,
 * focus is inside it, a finger or the mouse is on it, the tab is hidden, the row
 * is off screen, or the visitor asked for reduced motion; any arrow, key, drag,
 * swipe or wheel starts the wait again, so it never fights the visitor.
 */
(function () {
  'use strict';

  var CONFIG = 'script[type="application/json"][data-ather-carousel-nav]';
  // the same config as an attribute on the carousel itself - for markup that
  // Horizon morphs in later (morph does not bring <script> elements across)
  var CONFIG_ATTR = 'data-ather-carousel-nav-config';
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function format(template, index, length) {
    return String(template).replace('{{ index }}', index).replace('{{ length }}', length);
  }

  // `own`: a config marked data-scoped belongs to the carousel around it, so a
  // section can hold several (the header: menu drawer row and mega menu row)
  function setup(section, own) {
    if (!section || (!own && section.querySelector('.ather-carousel-nav:not([data-owned])'))) return;
    var cfgEl = own || section.querySelector(CONFIG);
    if (!cfgEl) return;
    var fromAttr = cfgEl.hasAttribute(CONFIG_ATTR);
    var cfg;
    try {
      cfg = JSON.parse(fromAttr ? cfgEl.getAttribute(CONFIG_ATTR) : cfgEl.textContent);
    } catch (e) {
      return;
    }
    var carousel = fromAttr ? cfgEl : own ? cfgEl.closest(cfg.carousel) : section.querySelector(cfg.carousel || '.resource-list__carousel');
    if (!carousel || carousel.__atherNav) return;
    carousel.__atherNav = true;
    var scroller = cfg.scroller
      ? carousel.querySelector(cfg.scroller)
      : carousel.querySelector('slideshow-slides') || carousel.querySelector('[class*="slides"]');
    if (!scroller) return;
    // no arrows and no indicator: keys and drag can still be wired
    var showNav = !!(cfg.arrows || cfg.indicator);
    if (!showNav && !cfg.keys && !cfg.drag) return;
    var slideClass = cfg.slide || 'resource-list__slide';

    // read live: a rail may be reordered after the nav is built
    function slideList() {
      return Array.prototype.slice.call(scroller.children).filter(function (n) {
        return n.classList && n.classList.contains(slideClass);
      });
    }
    var slides = slideList();
    var total = slides.length;
    if (total < 2) return;
    var multi = cfg.multi === true;

    var rtl = getComputedStyle(scroller).direction === 'rtl';

    var nav = document.createElement('div');
    nav.className = 'ather-carousel-nav';
    nav.setAttribute('role', 'group');
    nav.setAttribute('aria-label', cfg.label);
    if (own) nav.setAttribute('data-owned', '');
    var loop = cfg.loop === true && multi;
    var offset = 0; // cards moved from the front to the back (loop)

    function button(cls, label, glyphFlip) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ather-carousel-nav__arrow ' + cls;
      b.setAttribute('aria-label', label);
      b.innerHTML =
        '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"' +
        (glyphFlip ? ' style="transform:scaleX(-1)"' : '') +
        '><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      return b;
    }

    // The glyph points "back" in reading order: left in LTR, right in RTL.
    var prev = cfg.arrows ? button('ather-carousel-nav__prev', cfg.prev, rtl) : null;
    var next = cfg.arrows ? button('ather-carousel-nav__next', cfg.next, !rtl) : null;

    var status = document.createElement('div');
    status.className = 'ather-carousel-nav__status';
    var dots = [];
    var counter = null;
    // The spoken position, whichever indicator is shown.
    var live = document.createElement('span');
    live.className = 'visually-hidden';
    live.setAttribute('aria-live', 'polite');

    if (cfg.indicator && cfg.style === 'dots') {
      for (var i = 0; i < total; i++) {
        (function (n) {
          var d = document.createElement('button');
          d.type = 'button';
          d.className = 'ather-carousel-nav__dot';
          d.setAttribute('aria-label', format(cfg.status, n + 1, total));
          d.addEventListener('click', function () {
            go(n);
          });
          dots.push(d);
          status.appendChild(d);
        })(i);
      }
    } else if (cfg.indicator) {
      counter = document.createElement('span');
      counter.className = 'ather-carousel-nav__counter';
      counter.setAttribute('aria-hidden', 'true');
      status.appendChild(counter);
    }
    status.appendChild(live);

    if (prev) nav.appendChild(prev);
    nav.appendChild(status);
    if (next) nav.appendChild(next);
    if (!showNav) nav.hidden = true;
    carousel.appendChild(nav);

    function step() {
      if (total < 2) return scroller.clientWidth;
      if (multi) slides = slideList();
      var a = slides[0].getBoundingClientRect();
      var b = slides[1].getBoundingClientRect();
      return Math.abs(b.left - a.left) || scroller.clientWidth;
    }

    // cards fully in view at once (1 on the phone carousels)
    function perView() {
      return multi ? Math.max(1, Math.round((scroller.clientWidth + 1) / step())) : 1;
    }

    function atEnd() {
      var max = scroller.scrollWidth - scroller.clientWidth;
      return max <= 2 || Math.abs(scroller.scrollLeft) >= max - 2;
    }

    function index() {
      var max = scroller.scrollWidth - scroller.clientWidth;
      var pos = Math.abs(scroller.scrollLeft);
      // the last card may not reach the start edge; the end of travel is it
      if (max > 0 && pos >= max - 2) return total - perView();
      return Math.max(0, Math.min(total - 1, Math.round(pos / step())));
    }

    function go(n) {
      n = Math.max(0, Math.min(total - 1, n));
      var target = n * step();
      scroller.scrollTo({
        left: rtl ? -target : target,
        behavior: reduce && reduce.matches ? 'auto' : 'smooth',
      });
    }

    var last = -1;
    var ownTab = cfg.keys && !scroller.hasAttribute('tabindex');
    function update() {
      if (multi) {
        // nothing to scroll: no nav at all
        var fits = scroller.scrollWidth - scroller.clientWidth <= 2;
        nav.hidden = fits || !showNav;
        // a row that fits is not a tab stop of its own
        if (ownTab) {
          if (fits) scroller.removeAttribute('tabindex');
          else scroller.setAttribute('tabindex', '0');
        }
        if (fits) {
          last = -1;
          return;
        }
      }
      var i = loop ? (((index() + offset) % total) + total) % total : index();
      if (i === last) return;
      last = i;
      if (prev) prev.disabled = !loop && i === 0;
      if (next) next.disabled = loop ? false : multi ? atEnd() : i === total - 1;
      if (counter) counter.textContent = i + 1 + ' / ' + total;
      dots.forEach(function (d, k) {
        if (k === i) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      live.textContent = format(cfg.status, i + 1, total);
      nav.setAttribute('data-index', String(i + 1));
    }

    var ticking = false;
    scroller.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          update();
        });
      },
      { passive: true }
    );
    // a smooth scroll that ends between frames still lands on the right card
    scroller.addEventListener('scrollend', update, { passive: true });

    // an instant scroll that must not snap or glide on its way
    function jump(pos) {
      var snap = scroller.style.scrollSnapType;
      scroller.style.scrollSnapType = 'none';
      scroller.scrollTo({ left: rtl ? -pos : pos, behavior: 'instant' });
      requestAnimationFrame(function () {
        scroller.style.scrollSnapType = snap;
      });
    }

    function forward() {
      if (!loop || !atEnd()) return go(index() + 1);
      // the first card goes behind the last; hold the view still, then glide on
      var s = step();
      var pos = Math.abs(scroller.scrollLeft);
      slides = slideList();
      scroller.appendChild(slides[0]);
      offset = (offset + 1) % total;
      jump(pos - s);
      last = -1;
      requestAnimationFrame(function () {
        go(index() + 1);
      });
    }

    function backward() {
      if (!loop || Math.abs(scroller.scrollLeft) > 2) return go(index() - 1);
      var s = step();
      slides = slideList();
      scroller.insertBefore(slides[slides.length - 1], slides[0]);
      offset = (offset - 1 + total) % total;
      jump(s);
      last = -1;
      requestAnimationFrame(function () {
        go(0);
      });
    }

    if (prev) prev.addEventListener('click', function () { backward(); touched(); });
    if (next) next.addEventListener('click', function () { forward(); touched(); });

    if (multi && typeof ResizeObserver === 'function') {
      new ResizeObserver(function () {
        last = -1;
        update();
      }).observe(scroller);
    }

    if (cfg.keys) {
      if (ownTab && !multi) scroller.setAttribute('tabindex', '0');
      scroller.addEventListener('keydown', function (e) {
        // ArrowRight means "next" in LTR and "previous" in RTL: follow the screen
        var dir = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[e.key];
        if (dir) (dir > 0 ? forward : backward)();
        else if (e.key === 'Home') go(0);
        else if (e.key === 'End') go(total - 1);
        else return;
        e.preventDefault();
        touched();
      });
    }

    if (cfg.drag) {
      // mouse only; a drag of more than 6px swallows the click it ends with
      var startX = 0, startLeft = 0, dragging = false, moved = false;
      scroller.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startLeft = scroller.scrollLeft;
      });
      scroller.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - startX;
        if (!moved && Math.abs(dx) < 6) return;
        if (!moved) {
          moved = true;
          scroller.setPointerCapture(e.pointerId);
          scroller.classList.add('is-dragging');
        }
        scroller.scrollLeft = startLeft - dx;
      });
      var end = function (e) {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        scroller.classList.remove('is-dragging');
        if (scroller.hasPointerCapture(e.pointerId)) scroller.releasePointerCapture(e.pointerId);
        go(index());
      };
      scroller.addEventListener('pointerup', end);
      scroller.addEventListener('pointercancel', end);
      scroller.addEventListener(
        'click',
        function (e) {
          if (!moved) return;
          moved = false;
          e.preventDefault();
          e.stopPropagation();
        },
        true
      );
      scroller.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });
    }

    // ---- autoplay -------------------------------------------------------
    var timer = null;
    var hold = { hover: false, focus: false, press: false, away: false };
    var every = (parseFloat(cfg.autoplay) || 0) * 1000;
    var minWidth = cfg.autoplayMin || 990;
    function allowed() {
      return (
        every > 0 &&
        !(reduce && reduce.matches) &&
        window.innerWidth >= minWidth &&
        !document.hidden &&
        !hold.hover && !hold.focus && !hold.press && !hold.away &&
        scroller.scrollWidth - scroller.clientWidth > 2
      );
    }
    function schedule() {
      clearTimeout(timer);
      timer = null;
      if (!every) return;
      timer = setTimeout(function () {
        if (allowed()) forward();
        schedule();
      }, every);
    }
    // a visitor's move starts the wait again
    function touched() {
      if (every) schedule();
    }
    if (every) {
      carousel.setAttribute('data-autoplay', String(every / 1000));
      var box = carousel;
      box.addEventListener('pointerenter', function (e) {
        if (e.pointerType === 'mouse') hold.hover = true;
      });
      box.addEventListener('pointerleave', function (e) {
        if (e.pointerType === 'mouse') {
          hold.hover = false;
          touched();
        }
      });
      box.addEventListener('focusin', function () {
        hold.focus = true;
      });
      box.addEventListener('focusout', function (e) {
        if (!box.contains(e.relatedTarget)) {
          hold.focus = false;
          touched();
        }
      });
      scroller.addEventListener('pointerdown', function () {
        hold.press = true;
      });
      var release = function () {
        if (!hold.press) return;
        hold.press = false;
        touched();
      };
      document.addEventListener('pointerup', release);
      document.addEventListener('pointercancel', release);
      scroller.addEventListener('touchstart', function () { hold.press = true; }, { passive: true });
      scroller.addEventListener('touchend', release, { passive: true });
      scroller.addEventListener('wheel', touched, { passive: true });
      document.addEventListener('visibilitychange', touched);
      if (typeof IntersectionObserver === 'function') {
        var w = document.querySelector('.page-wrapper');
        var root = w && /(auto|scroll)/.test(getComputedStyle(w).overflowY) ? w : null;
        new IntersectionObserver(function (entries) {
          hold.away = !entries[0].isIntersecting;
          if (!hold.away) touched();
        }, { root: root, threshold: 0.35 }).observe(scroller);
      }
      schedule();
    }

    update();
  }

  function scan(root) {
    var seen = {};
    var cfgs = (root || document).querySelectorAll(CONFIG + ', [' + CONFIG_ATTR + ']');
    for (var i = 0; i < cfgs.length; i++) {
      var sec = cfgs[i].closest('.shopify-section');
      if (sec && (cfgs[i].hasAttribute('data-scoped') || cfgs[i].hasAttribute(CONFIG_ATTR))) {
        setup(sec, cfgs[i]);
        continue;
      }
      if (sec && !seen[sec.id]) {
        seen[sec.id] = true;
        setup(sec);
      }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();

  window.AtherCarouselNav = { scan: scan };

  // Horizon renders some parts twice (the header's menu drawer gets its
  // products on a second render): wire up config that arrives later
  var queued = false;
  new MutationObserver(function (records) {
    if (queued) return;
    var sel = CONFIG + ', [' + CONFIG_ATTR + ']';
    var added = records.some(function (r) {
      if (r.type === 'attributes') return true;
      return Array.prototype.some.call(r.addedNodes, function (n) {
        return n.nodeType === 1 && (n.matches(sel) || (n.querySelector && n.querySelector(sel)));
      });
    });
    if (!added) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      scan();
    });
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: [CONFIG_ATTR] });

  // Theme editor: a re-rendered section brings fresh markup to wire up.
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });
})();
